import React, { useEffect, useRef, useState } from "react";
import Card from "../../../elements/Card";
import Button from "../../../form/Button";
import { classNames } from "../../../../helpers/classNames";
import Radio from "../../../form/Radio";
import Input from "../../../form/Input";
import Textarea from "../../../form/Textarea";
import { useParams } from "react-router-dom";
import useClickOutside from "../../../../hooks/useClickOutside";
import { useTranslation } from "react-i18next";

const ReadOnly = ({ data }) => {
  const { t } = useTranslation();
  return (
    <Card
      title={t("Helpful Tips")}
      bodyClass={"space-y-6"}
      className={" py-4 md:py-8 px-3 md:px-6"}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <h3 className="font-semibold text-lg capitalize">
            {t("check in unit")}
          </h3>
          <div className="text-text-gradient-red font-medium">
            {data?.checkInUnit || "N/A"}
          </div>
        </div>
        <div className="space-y-1">
          <h3 className="font-semibold text-lg capitalize">
            {t("check in duration")}
          </h3>
          <div className="text-text-gradient-red font-medium">
            {data?.checkInDuration || "N/A"}
          </div>
        </div>
        <div className="space-y-1">
          <h3 className="font-semibold text-lg capitalize">
            {t("age restriction type")}
          </h3>
          <div className="text-text-gradient-red font-medium">
            {data?.ageRestrictionType || "N/A"}
          </div>
        </div>
        <div className="space-y-1">
          <h3 className="font-semibold text-lg capitalize">
            {t("max age allow")}
          </h3>
          <div className="text-text-gradient-red font-medium">
            {data?.ageRestrictionValue || "N/A"}
          </div>
        </div>
        <div className="space-y-1">
          <h3 className="font-semibold text-lg capitalize">
            {t("parking type")}
          </h3>
          <div className="text-text-gradient-red font-medium">
            {data?.parkingType || "N/A"}
          </div>
        </div>
      </div>
      <div className="space-y-1">
        <h3 className="font-semibold text-lg capitalize">{t("faqs")}</h3>
        <div className="space-y-2 text-text-gradient-red font-medium">
          {data?.faqs?.length > 0
            ? data?.faqs?.map((faq, i) => (
                <div
                  key={i}
                  className="border border-primary p-1 rounded-md space-y-1"
                >
                  <div className="bg-primary/30 p-3 rounded-md">
                    {faq?.title}
                  </div>
                  <div className="p-3 bg-surface-04 rounded-md">
                    {faq?.excerpt}
                  </div>
                </div>
              ))
            : "N/A"}
        </div>
      </div>
    </Card>
  );
};

const Tips = ({
  values = {},
  handleChange = () => {},
  setFieldsValue = () => {},
  errors = {},
  formValidate = () => {},
}) => {
  const { t } = useTranslation();
  const divRef = useRef(null);
  const { eventId } = useParams();
  const [showAgeDetails, setShowAgeDetails] = useState(false);
  const [showEntryDetails, setShowEntryDetails] = useState(false);
  const [showParkingDetails, setShowParkingDetails] = useState(false);
  const [readOnly, setReadOnly] = useState(false);

  const hasErrors = () => {
    const errorFields = ["faqs"];

    // Check for direct field errors
    const hasDirectErrors = errorFields.some((field) => errors[field]);

    // Check for FAQ errors
    const hasFaqErrors =
      errors.faqs &&
      Array.isArray(errors.faqs) &&
      errors.faqs.some(
        (faqError) => faqError && (faqError.title || faqError.excerpt)
      );

    return hasDirectErrors || hasFaqErrors;
  };

  useClickOutside(divRef, () => {
    if (eventId && !hasErrors()) {
      setReadOnly(true);
    } else {
      setReadOnly(false);
    }
  });

  useEffect(() => {
    if (eventId) {
      setReadOnly(true);
    }
  }, [eventId]);

  useEffect(() => {
    if (hasErrors()) {
      setReadOnly(false);
    }
  }, [errors]);

  // Initialize faqs array if it doesn't exist
  useEffect(() => {
    if (!values.faqs) {
      setFieldsValue({ faqs: [] });
    }
  }, [values.faqs, setFieldsValue]);

  useEffect(() => {
    if (values?.ageRestrictionType) {
      setShowAgeDetails(true);
    }
    if (values?.checkInUnit) {
      setShowEntryDetails(true);
    }
    if (values?.parkingType) {
      setShowParkingDetails(true);
    }
  }, [values]);

  // Helper function to update FAQ at specific index
  const updateFaqAtIndex = (index, field, value) => {
    const updatedFaqs = values.faqs.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    setFieldsValue({ faqs: updatedFaqs });
  };

  // Helper function to remove FAQ at specific index
  const removeFaqAtIndex = (index) => {
    const updatedFaqs = values.faqs.filter((_, i) => i !== index);
    setFieldsValue({ faqs: updatedFaqs });
  };

  // Helper function to add new FAQ
  const addNewFaq = () => {
    const newFaq = { title: "", excerpt: "" };
    const updatedFaqs = [...(values.faqs || []), newFaq];
    setFieldsValue({ faqs: updatedFaqs });
  };

  return (
    <div
      ref={divRef}
      onClick={() => {
        if (eventId) {
          setReadOnly(false);
        }
      }}
    >
      {readOnly ? (
        <ReadOnly data={values} />
      ) : (
        <Card
          title={t("Helpful Tips")}
          bodyClass={"space-y-6"}
          className={"py-4 md:py-8 px-3 md:px-6"}
        >
          <div className="relative space-y-3">
            <div className="text-lg text-text-secondary">
              {t(
                "Showcase key event details here, including highlights and FAQs to help attendees prepare."
              )}
            </div>
            <div className="flex items-center gap-4">
              <Button
                buttonLabel={t("Add age")}
                labelClasses={"!capitalize"}
                buttonClasses={classNames(
                  "!bg-none !bg-surface-04 hover:!bg-magnolia hover:!border-gray-200 !rounded-md !h-8 !px-3 !text-deep-purple",
                  showAgeDetails ? "!bg-magnolia !border-gray-200" : ""
                )}
                buttonIconRight={classNames(
                  "fa-regular",
                  showAgeDetails ? "fa-minus" : "fa-plus"
                )}
                buttonIconPosition={"right"}
                buttonFunction={() => setShowAgeDetails(!showAgeDetails)}
              />
              <Button
                buttonLabel={t("Add entry time")}
                labelClasses={"!capitalize"}
                buttonClasses={classNames(
                  "!bg-none !bg-surface-04 hover:!bg-magnolia hover:!border-gray-200 !rounded-md !h-8 !px-3 !text-deep-purple",
                  showEntryDetails ? "!bg-magnolia !border-gray-200" : ""
                )}
                buttonIconRight={classNames(
                  "fa-regular",
                  showEntryDetails ? "fa-minus" : "fa-plus"
                )}
                buttonIconPosition={"right"}
                buttonFunction={() => setShowEntryDetails(!showEntryDetails)}
              />
              <Button
                buttonLabel={t("Add parking details")}
                labelClasses={"!capitalize"}
                buttonClasses={classNames(
                  "!bg-none !bg-surface-04 hover:!bg-magnolia hover:!border-gray-200 !rounded-md !h-8 !px-3 !text-deep-purple",
                  showParkingDetails ? "!bg-magnolia !border-gray-200" : ""
                )}
                buttonIconRight={classNames(
                  "fa-regular",
                  showParkingDetails ? "fa-minus" : "fa-plus"
                )}
                buttonIconPosition={"right"}
                buttonFunction={() =>
                  setShowParkingDetails(!showParkingDetails)
                }
              />
            </div>
          </div>

          <div className="rounded-lg bg-surface-04 p-5 space-y-6">
            {(showAgeDetails || showEntryDetails || showParkingDetails) && (
              <div className="text-xl font-bold text-deep-purple">
                {t("Include key features of your event")}
              </div>
            )}
            <div className="space-y-10">
              {showEntryDetails && (
                <div className="space-y-4">
                  <div className="text-text-secondary text-lg">
                    {t("What time can attendees check in before the event?")}
                  </div>
                  <div className="flex items-center gap-3">
                    <Radio
                      radioName={"checkInUnit"}
                      checked={values?.checkInUnit === "min"}
                      value={"min"}
                      divClass="hidden"
                      radioLableClass={
                        "border border-transparent peer-checked:border-deep-purple bg-white h-10 px-4 flex items-center justify-center py-2 rounded-md !text-lg !text-deep-purple"
                      }
                      radioLabel={"Minutes"}
                      onChange={(e) =>
                        handleChange({
                          target: {
                            name: e.target.name,
                            value: e.target.value,
                          },
                        })
                      }
                    />
                    <Radio
                      radioName={"checkInUnit"}
                      checked={values?.checkInUnit === "hr"}
                      value={"hr"}
                      divClass="hidden"
                      radioLableClass={
                        "border border-transparent peer-checked:border-deep-purple bg-white h-10 px-4 flex items-center justify-center py-2 rounded-md !text-lg !text-deep-purple"
                      }
                      radioLabel={"Hours"}
                      onChange={(e) =>
                        handleChange({
                          target: {
                            name: e.target.name,
                            value: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                  <div className="w-48">
                    <Input
                      label={``}
                      onChange={handleChange}
                      value={values?.checkInDuration}
                      inputName={"checkInDuration"}
                      inputPrefix={<i className="fa-regular fa-clock"></i>}
                      inputClasses={"!pl-10 !h-12"}
                      {...formValidate(errors, "checkInDuration")}
                    />
                  </div>
                </div>
              )}
              {showAgeDetails && (
                <div className="space-y-4">
                  <div className="text-text-secondary text-lg">
                    {t("Are there any age restrictions?")}
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="relative">
                      <Radio
                        radioName={"ageRestrictionType"}
                        value="restricted"
                        mainClasses={classNames(
                          "!flex-row-reverse p-5 rounded-md bg-white border",
                          values?.ageRestrictionType === "restricted"
                            ? "border-gray-200"
                            : "border-transparent"
                        )}
                        divClass={
                          "!mr-0 !ml-3 !border-2 !border-gray-200 peer-checked:!border-gray-200"
                        }
                        radioLabel={
                          <span className="flex items-center gap-3 font-normal text-base text-deep-purple">
                            <i className="fa-fw fa-regular fa-circle-check text-2xl"></i>
                            {t("Minimum Age")}
                          </span>
                        }
                        checked={values?.ageRestrictionType === "restricted"}
                        onChange={(e) =>
                          handleChange({
                            target: {
                              name: e.target.name,
                              value: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="relative">
                      <Radio
                        radioName={"ageRestrictionType"}
                        value="all"
                        mainClasses={classNames(
                          "!flex-row-reverse p-5 rounded-md bg-white border",
                          values?.ageRestrictionType === "all"
                            ? "border-gray-200"
                            : "border-transparent"
                        )}
                        divClass={
                          "!mr-0 !ml-3 !border-2 !border-gray-200 peer-checked:!border-gray-200"
                        }
                        radioLabel={
                          <span className="flex items-center gap-3 font-normal text-base text-deep-purple">
                            <i className="fa-fw fa-regular fa-person text-2xl"></i>
                            {t("Age Requirement")}
                          </span>
                        }
                        checked={values?.ageRestrictionType === "all"}
                        onChange={(e) =>
                          handleChange({
                            target: {
                              name: e.target.name,
                              value: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="relative">
                      <Radio
                        radioName={"ageRestrictionType"}
                        value="guardian"
                        mainClasses={classNames(
                          "!flex-row-reverse p-5 rounded-md bg-white border",
                          values?.ageRestrictionType === "guardian"
                            ? "border-gray-200"
                            : "border-transparent"
                        )}
                        divClass={
                          "!mr-0 !ml-3 !border-2 !border-gray-200 peer-checked:!border-gray-200"
                        }
                        radioLabel={
                          <span className="flex items-center gap-3 font-normal text-base text-deep-purple">
                            <i className="fa-fw fa-regular fa-people-pants text-2xl"></i>
                            {t("Guardian Required")}
                          </span>
                        }
                        checked={values?.ageRestrictionType === "guardian"}
                        onChange={(e) =>
                          handleChange({
                            target: {
                              name: e.target.name,
                              value: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="text-text-secondary text-lg">
                    {t("Age Requirement?")}
                  </div>
                  <div className="flex gap-2 justify-between">
                    {Array.from({ length: 9 }, (_, i) => i + 13)?.map(
                      (item, i) => (
                        <Button
                          key={item}
                          buttonClasses={classNames(
                            "gap-0 bg-white rounded-md !w-full !h-16 border border-transparent !text-deep-purple",
                            item?.toString() ===
                              values?.ageRestrictionValue?.toString()
                              ? "!border-gray-200 !bg-magnolia"
                              : ""
                          )}
                          buttonLabelClasses={"text-deep-purple text-base"}
                          buttonLabel={item + "+"}
                          buttonFunction={() => {
                            handleChange({
                              target: {
                                name: "ageRestrictionValue",
                                value: item?.toString(),
                              },
                            });
                          }}
                        />
                      )
                    )}
                  </div>
                </div>
              )}
              {showParkingDetails && (
                <div className="space-y-4">
                  <div className="text-text-secondary text-lg">
                    {t("Parking Available?")}
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="relative">
                      <Radio
                        radioName={"parkingType"}
                        value="free"
                        mainClasses={classNames(
                          "!flex-row-reverse p-5 rounded-md bg-white border",
                          values?.parkingType === "free"
                            ? "border-gray-200"
                            : "border-transparent"
                        )}
                        divClass={
                          "!mr-0 !ml-3 !border-2 !border-gray-200 peer-checked:!border-gray-200"
                        }
                        radioLabel={
                          <span className="flex items-center gap-3 font-normal text-base text-deep-purple">
                            <i className="fa-fw fa-regular fa-square-parking text-2xl"></i>
                            {t("Zero-cost Parking")}
                          </span>
                        }
                        checked={values?.parkingType === "free"}
                        onChange={(e) =>
                          handleChange({
                            target: {
                              name: e.target.name,
                              value: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="relative">
                      <Radio
                        radioName={"parkingType"}
                        value="paid"
                        mainClasses={classNames(
                          "!flex-row-reverse p-5 rounded-md bg-white border",
                          values?.parkingType === "paid"
                            ? "border-gray-200"
                            : "border-transparent"
                        )}
                        divClass={
                          "!mr-0 !ml-3 !border-2 !border-gray-200 peer-checked:!border-gray-200"
                        }
                        radioLabel={
                          <span className="flex items-center gap-3 font-normal text-base text-deep-purple">
                            <i className="fa-fw fa-regular fa-circle-parking text-2xl"></i>
                            {t("Parking Fee")}
                          </span>
                        }
                        checked={values?.parkingType === "paid"}
                        onChange={(e) =>
                          handleChange({
                            target: {
                              name: e.target.name,
                              value: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="relative">
                      <Radio
                        radioName={"parkingType"}
                        value="unavailable"
                        mainClasses={classNames(
                          "!flex-row-reverse p-5 rounded-md bg-white border",
                          values?.parkingType === "unavailable"
                            ? "border-gray-200"
                            : "border-transparent"
                        )}
                        divClass={
                          "!mr-0 !ml-3 !border-2 !border-gray-200 peer-checked:!border-gray-200"
                        }
                        radioLabel={
                          <span className="flex items-center gap-3 font-normal text-base text-deep-purple">
                            <i className="fa-fw fa-regular fa-ban-parking text-2xl"></i>
                            {t("Parking Unavailable")}
                          </span>
                        }
                        checked={values?.parkingType === "unavailable"}
                        onChange={(e) =>
                          handleChange({
                            target: {
                              name: e.target.name,
                              value: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* FAQ Section */}
            <div className="space-y-4">
              <div className="text-text-secondary text-lg">
                {t("Frequently asked questions")}
              </div>

              {/* Display existing FAQs */}
              {values?.faqs &&
                values.faqs.length > 0 &&
                values.faqs.map((faq, i) => (
                  <div
                    key={`faq-${i}`}
                    className="divide-x divide-slate-300 flex gap-3"
                  >
                    <div className="space-y-2 flex-grow">
                      <div className="text-text-secondary text-base font-medium">
                        {t("Question")} {i + 1}
                      </div>
                      <Input
                        value={faq?.title || ""}
                        inputPlaceholder={`${t("Question")} ${i + 1}`}
                        onChange={(e) => {
                          updateFaqAtIndex(i, "title", e.target.value);
                        }}
                        {...formValidate(errors, "faqs", i, "title")}
                      />
                      <Textarea
                        inputValue={faq?.excerpt || ""}
                        inputPlaceholder={`${t("Answer for question")} ${
                          i + 1
                        }`}
                        onChange={(e) => {
                          updateFaqAtIndex(i, "excerpt", e.target.value);
                        }}
                        {...formValidate(errors, "faqs", i, "excerpt")}
                      />
                    </div>
                    <div className="w-20 flex items-center justify-center">
                      <Button
                        buttonClasses={
                          "bg-slate-100 rounded-full h-12 w-12 aspect-square"
                        }
                        buttonLabelClasses={"text-base"}
                        buttonIcon={
                          "fa-regular fa-trash-can text-base text-deep-purple"
                        }
                        buttonFunction={() => removeFaqAtIndex(i)}
                      />
                    </div>
                  </div>
                ))}

              {/* Add FAQ button */}
              <Button
                variant="primary"
                buttonLabel={"Enter a question"}
                buttonClasses={
                  "bg-primary-gradient hover:opacity-90 transition-all duration-300 !text-white"
                }
                buttonLabelClasses={"!text-white"}
                buttonFunction={(e) => {
                  e.preventDefault(); // Prevent form submission
                  addNewFaq();
                }}
              />
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default Tips;
